import { useRouter } from 'next/router';
import { useRef, useEffect, useState } from 'react';
import { useDebounce } from '@/lib/hooks/use-debounce';
import AISearchIcon from '../icons/ai-search-icon';
import { useProductLoader } from './product-loader';
import { Input, cn, Button } from '@nextui-org/react';
import { atom, useAtom } from 'jotai';
import { trackEvent } from '@/data/utils/utils';
import InstantSearchResults from '../search/instant-search-results';
import { useProducts } from '@/data/product';
import { sortables } from '@/lib/constants';
import { SortOrder } from '@/types';
import { Search } from 'lucide-react';
import Portal from '../ui/portal';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';

// Create separate atoms for different search contexts
const marketplaceSearchAtom = atom('');
const headerSearchAtom = atom('');
const isSearchFocusedAtom = atom(false);

export function useSearchText(
  context: 'marketplace' | 'header' = 'marketplace'
) {
  const [marketplaceSearch, setMarketplaceSearch] = useAtom(
    marketplaceSearchAtom
  );
  const [headerSearch, setHeaderSearch] = useAtom(headerSearchAtom);

  return {
    searchText: context === 'marketplace' ? marketplaceSearch : headerSearch,
    setSearchText:
      context === 'marketplace' ? setMarketplaceSearch : setHeaderSearch,
    setMarketplaceSearch,
    setHeaderSearch,
  };
}

export function useSearchFocus() {
  return useAtom(isSearchFocusedAtom);
}

export default function ProductSearchBar({
  id = 'searchBar',
  searchRef,
  selectedTab,
  className = '',
  classNameInputWrapper = '',
  size = undefined,
  onFocus,
  isInstantSearch = true,
  context = 'marketplace',
}: any) {
  const router = useRouter();
  const { searchText, setSearchText, setMarketplaceSearch } =
    useSearchText(context);
  const debouncedSearch = useDebounce(searchText, 600);
  const { setIsLoading } = useProductLoader();
  const [showResults, setShowResults] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useAtom(isSearchFocusedAtom);

  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm' || breakpoint === 'xs';

  // Add this back - Ref to track initial load
  const initialLoad = useRef(true);

  // Add this effect back - Initialize search from URL
  useEffect(() => {
    if (
      initialLoad.current &&
      router.query.search &&
      context === 'marketplace'
    ) {
      const searchQuery = router.query.search as string;
      setSearchText(searchQuery);
      setMarketplaceSearch(searchQuery);
      initialLoad.current = false;
    }
  }, [router.query.search, context, setSearchText, setMarketplaceSearch]);

  // Handle search navigation
  const handleSearchNavigation = (searchQuery: string) => {
    // Always update marketplace search state first
    setMarketplaceSearch(searchQuery);

    // Navigate to marketplace with search
    return router
      .push({
        pathname: '/products',
        query: { search: searchQuery },
      })
      .then(() => {
        // Clear search text only in header/home context
        if (context !== 'marketplace') {
          setSearchText('');
        }
        setShowResults(false);
      });
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    // Only set loading in marketplace context
    if (context === 'marketplace') {
      setIsLoading(true);
    }
    setShowResults(true);
  };

  // Handle key press (Enter)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchText.trim()) {
      if (context === 'marketplace') {
        // In marketplace, just update the URL
        handleSearchNavigation(searchText);
      } else {
        // In header/home, navigate to marketplace
        handleSearchNavigation(searchText);
      }
    }
  };

  // Handle show all results
  const handleShowAllResults = () => {
    if (searchText.trim()) {
      handleSearchNavigation(searchText);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    if (searchText.trim()) {
      handleSearchNavigation(searchText);
    }
  };

  // Handle clear
  const handleClear = () => {
    setSearchText('');
    setShowResults(false);

    if (context === 'marketplace') {
      setMarketplaceSearch('');
      // Clear search from URL in marketplace
      const { search, ...rest } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: rest,
        },
        undefined,
        { scroll: false }
      );
    }
  };

  // Update URL in marketplace context when debounced search changes
  useEffect(() => {
    if (context === 'marketplace' && debouncedSearch !== undefined) {
      if (debouncedSearch === '') {
        // Clear search from URL
        const { search, ...rest } = router.query;
        router.push(
          {
            pathname: router.pathname,
            query: rest,
          },
          undefined,
          { scroll: false }
        );
      } else {
        // Update URL with search
        router.push(
          {
            pathname: router.pathname,
            query: { ...router.query, search: debouncedSearch },
          },
          undefined,
          { scroll: false }
        );
      }
    }
  }, [debouncedSearch, context]);

  // Handle click outside to close results
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const popoverElement = document.querySelector('.search-results-popover');
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node) &&
        !popoverElement?.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    }

    function updatePosition() {
      if (searchBarRef.current && showResults) {
        const searchBarRect = searchBarRef.current.getBoundingClientRect();
        const popoverElement = document.querySelector(
          '.search-results-popover'
        ) as HTMLElement;
        if (popoverElement) {
          popoverElement.style.left = `${searchBarRect.left}px`;
          popoverElement.style.top = `${
            searchBarRect.bottom + window.scrollY
          }px`;
          popoverElement.style.width = `${searchBarRect.width}px`;
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showResults]);

  // Only fetch products for instant search results
  const { products, isLoading: isProductsLoading } = useProducts(
    {
      name: searchText,
      // orderBy: sortable.orderBy,
      // sortedBy: sortable.sortedBy as SortOrder,
      limit: 5,
    },
    {
      enabled: Boolean(searchText && showResults && isInstantSearch),
      refetchOnWindowFocus: false,
    }
  );

  // Define consistent text
  const searchPlaceholder = 'Search prompts or models';
  const buttonText = 'Search';

  const handleFocus = () => {
    setShowResults(true);
    setIsSearchFocused(true);
    onFocus && onFocus();
  };

  const handleBlur = () => {
    setIsSearchFocused(false);
  };

  return (
    <div ref={searchBarRef} className="relative">
      <div className="relative flex items-center">
        <Input
          id={id}
          type="text"
          ref={searchRef}
          value={searchText}
          startContent={
            context === 'header' ? null : (
              <AISearchIcon
                className="h-6 w-6 text-dark-400 dark:text-dark-600"
                stroke="currentColor"
              />
            )
          }
          isClearable
          size={size}
          onClear={handleClear}
          autoFocus={!isMobile && context !== 'header'}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          autoComplete="off"
          onClick={handleFocus}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={searchPlaceholder}
          classNames={{
            input: cn(
              'mb-0 h-full custom-input !text-sm pl-3 pr-0 md:pr-3 !focus-visible:outline-none !focus:outline-none !border-none !bg-transparent !py-1',
              '[&::placeholder]:text-gray-400 [&::placeholder]:font-light [&::placeholder]:text-sm dark:[&::placeholder]:text-dark-600'
            ),
            inputWrapper: cn(
              'mb-0 custom-input !min-h-0 !focus-visible:outline-none !focus:outline-none !border !border-gray-200 dark:!border-dark-400 !shadow-none bg-default-400/20 dark:bg-dark-300/20 hover:bg-default-200 dark:hover:bg-dark-300/30 group-data-[focus=true]:bg-default-100',
              classNameInputWrapper,
              (context === 'header' || router.pathname === '/') &&
                context === 'header'
                ? '!pr-[48px]'
                : '!pr-[100px]'
            ),
            clearButton: 'text-default-400 dark:text-dark-600',
          }}
          className={cn(
            'custom-input !focus:outline-none !focus-visible:outline-none !my-0 w-full rounded-full bg-transparent text-sm text-dark !outline-none drop-shadow-sm focus:ring-0 dark:text-light-400',
            className
          )}
        />

        {(context === 'header' || router.pathname === '/') && (
          <Button
            className={cn(
              'absolute right-1 z-10 bg-brand text-white hover:bg-brand-dark',
              context === 'header'
                ? '!h-[32px] min-w-[40px] rounded-lg p-0' // Round shape for header
                : '-ml-[1px] !h-[42px] !rounded-lg px-4 md:min-w-[80px]' // Match input radius for non-header
            )}
            size="sm"
            disableRipple
            onClick={handleSearch}
          >
            <Search className="h-4 w-4" />
            {context !== 'header' && (
              <span className="ml-1 hidden md:block">{buttonText}</span>
            )}
          </Button>
        )}
      </div>

      {showResults && isInstantSearch && (
        <Portal>
          <div
            className="search-results-popover absolute z-50"
            style={{
              left: searchBarRef.current?.getBoundingClientRect().left ?? 0,
              top:
                (searchBarRef.current?.getBoundingClientRect().bottom ?? 0) +
                window.scrollY,
              width: searchBarRef.current?.offsetWidth ?? 0,
              zIndex: 999,
            }}
          >
            <InstantSearchResults
              products={products}
              isLoading={isProductsLoading}
              onResultClick={() => {
                setShowResults(false);
              }}
              onShowAllResults={() => {
                handleShowAllResults();
              }}
            />
          </div>
        </Portal>
      )}
    </div>
  );
}
